import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isRelativeUrl } from '@applied/utils/urls';
import classNames from 'classnames';

import './overflow-text-tooltip.scss';

import ResizableGroup, { useResizableGroup } from '../../stores/resizable_group';
import type { TooltipProps } from '../Tooltip/Tooltip';
import Tooltip from '../Tooltip/Tooltip';

import { useContainerRef, useInnerSpanRef } from './refs';

interface OverflowTextTooltipWithTextProps extends BaseOverflowTextTooltipProps {
  text: string;
  customElements?: never;
}

interface OverflowTextTooltipWithElementProps extends BaseOverflowTextTooltipProps {
  customElements?: JSX.Element;
  text?: never;
}

export interface BaseOverflowTextTooltipProps {
  className?: string;
  textClassName?: string;
  /**
   * Set to true if this OverflowTextTooltip should not observe width changes.
   * This is mostly useful for performance reasons - registering a lot of ResizeObservers
   * is pretty expensive.
   */
  isStatic?: boolean;
  textColor?: string;
  link?: string;
  openLinkInNewTab?: boolean;
  extraContent?: JSX.Element;
  onClick?: () => void;
  tooltipProps?: TooltipProps;
}

export type OverflowTextTooltipProps =
  | OverflowTextTooltipWithTextProps
  | OverflowTextTooltipWithElementProps;

/**
 * This can be used to show text that, when overflowed, is truncated with a tooltip.
 */
const OverflowTextTooltip: React.FC<OverflowTextTooltipProps> = (props) => {
  const {
    text,
    customElements,
    className,
    textClassName,
    isStatic,
    textColor,
    link,
    openLinkInNewTab,
    extraContent,
    onClick,
    tooltipProps,
  } = props;
  const [innerSpanWidth, setInnerSpanWidth] = useState<number | undefined>();
  const [containerWidth, setContainerWidth] = useState<number | undefined>();

  const innerSpanRef = useInnerSpanRef();
  const divRef = useContainerRef();

  const resizeContext = useResizableGroup();

  const customElementsOrText = customElements ?? text ?? '';

  useEffect(() => {
    setInnerSpanWidth(innerSpanRef.current?.offsetWidth);
  }, [customElementsOrText, innerSpanRef]);

  useEffect(() => {
    setContainerWidth(divRef.current?.offsetWidth);
  }, [divRef]);

  useEffect(() => {
    if (isStatic) {
      return;
    }

    const onResize = (): void => {
      setContainerWidth(divRef.current?.offsetWidth);
    };

    if (resizeContext.isDefault) {
      if (divRef.current) {
        const observer = new ResizableGroup(false /* isDefault */, divRef.current);
        observer.addCb(onResize);

        return (): void => observer.dispose();
      }
    } else {
      resizeContext.addCb(onResize);
      return (): void => {
        resizeContext.removeCb(onResize);
      };
    }
  }, [isStatic, divRef, resizeContext]);

  let textElement = (
    <span className="overflow-ellipsis" ref={innerSpanRef}>
      {customElementsOrText}
      {extraContent ? <span className="extra-content">{extraContent}</span> : null}
    </span>
  );

  if (innerSpanWidth && containerWidth && innerSpanWidth > containerWidth) {
    textElement = (
      <Tooltip {...tooltipProps} tooltip={tooltipProps?.tooltip ?? customElementsOrText}>
        {textElement}
      </Tooltip>
    );
  }

  const style = textColor ? { color: textColor } : {};

  let content: JSX.Element;
  if (link) {
    if (!isRelativeUrl(link)) {
      content = (
        <a
          href={link}
          className={textClassName}
          style={style}
          onClick={onClick}
          rel="noopener noreferrer"
          target="_blank"
        >
          {textElement}
        </a>
      );
    } else {
      content = (
        <Link
          to={link}
          className={textClassName}
          style={style}
          onClick={onClick}
          target={openLinkInNewTab ? '_blank' : undefined}
        >
          {textElement}
        </Link>
      );
    }
  } else {
    content = (
      <div className={textClassName} style={style} onClick={onClick}>
        {textElement}
      </div>
    );
  }

  return (
    <div className={classNames('overflow-text-tooltip-wrapper', className)} ref={divRef}>
      {content}
    </div>
  );
};

export default OverflowTextTooltip;
